import { RequestType } from "@internal/backstage-plugin-request-manager-common";
import { CircularProgress, Tooltip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import React from "react";
import { useCallback, useState } from "react";
import { ZodEffects, ZodObject } from "zod";
import { useRequestService } from "../../services/RequestService";

interface NameAvailabilityOptions {
    requestType: RequestType;
    fieldName: string;
}

interface FormDefinitionOptions {
    checkNameAvailability?: NameAvailabilityOptions;
}

const getStandardFieldProps = (errorMessage: string) => {
    return {
        error: !!errorMessage,
        helperText: errorMessage || ''
    };
};

const getLoadingFieldProps = () => {
    return {
        helperText: 'Checking availability...',
        InputProps: { endAdornment: <CircularProgress size={20} /> }
    };
};

const getErrorFieldProps = (errorMessage: string) => {
    return {
        error: true,
        helperText: errorMessage
    };
};

const getSuccessFieldProps = () => {
    return {
        InputProps: {
            endAdornment: (
                <Tooltip title="Available">
                    <CheckCircleIcon />
                </Tooltip>
            )
        }
    };
};

export const useFormDefinition = <T extends Record<string, any>> (
    schemaDefinition: ZodObject<any> | ZodEffects<ZodObject<any>>,
    initialValues: T,
    options?: FormDefinitionOptions
) => {
    const requestService = useRequestService();
    const [formValues, setFormValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [nameValidationState, setNameValidationState] = useState<{
        isChecking: boolean;
        customError: string | null;
        lastCheckedValue: string | null;
    }>({
        isChecking: false,
        customError: null,
        lastCheckedValue: null
    });

    const resetNameValidation = () => {
        setNameValidationState({ isChecking: false, customError: null, lastCheckedValue: null });
    };

    const setNameValidationLoading = () => {
        setNameValidationState({ isChecking: true, customError: null, lastCheckedValue: null });
    };

    const setNameValidationResult = (nameExists: boolean, validatedName: string) => {
        setNameValidationState({
            isChecking: false,
            customError: nameExists ? 'This identifier is already in use on this context. Please choose a different one.' : null,
            lastCheckedValue: nameExists ? null : validatedName
        });
    };

    const setNameValidationError = () => {
        setNameValidationState({ isChecking: false, customError: 'Could not validate name availability.', lastCheckedValue: null });
    };

    const nameAvailability = options?.checkNameAvailability;

    const validateForm = (values: T, field: string) => {
        const { error } = schemaDefinition.safeParse(values);
        const fieldError = error?.errors.find(err => err.path[0] === field);

        setErrors(prevValues => ({
            ...prevValues,
            [field]: fieldError?.message || ''
        }));
    };

    const handleNameValidation = useCallback(async (name?: string, owner?: string) => {
        if (!nameAvailability || !name || name.trim() === '' || !owner || owner.trim() === '') {
            resetNameValidation();
            return;
        }

        setNameValidationLoading();

        try {
            const nameExists = await requestService.existsByNameAndOwner(name, owner, nameAvailability.requestType);
            setNameValidationResult(nameExists, name);
        } catch (err) {
            setNameValidationError();
        }
    }, [nameAvailability, requestService]);

    const setFormValue = (field: string, value: any) => {
        const newValues = { ...formValues, [field]: value };
        setFormValues(newValues);
        validateForm(newValues, field);

        if (nameAvailability && field === nameAvailability?.fieldName && nameValidationState.customError) {
            setNameValidationState({ ...nameValidationState, customError: null, lastCheckedValue: null });
        }
    };

    const getFieldProps = useCallback((fieldName: string) => {
        if (!nameAvailability || fieldName !== nameAvailability?.fieldName) {
            return getStandardFieldProps(errors[fieldName]);
        }

        if (nameValidationState.isChecking) {
            return getLoadingFieldProps();
        }

        if (!!errors[fieldName]) {
            return getErrorFieldProps(errors[fieldName]);
        }

        if (!!nameValidationState.customError) {
            return getErrorFieldProps(nameValidationState.customError);
        }

        const currentValue = formValues[fieldName]?.trim() || '';
        const isNameFieldValid = currentValue && nameValidationState.lastCheckedValue === currentValue && !errors[fieldName];
        if (isNameFieldValid) {
            return getSuccessFieldProps();
        }

        return {};
    }, [errors, nameValidationState, nameAvailability, formValues]);

    const checkNameAvailability = useCallback((name?: string, owner?: string) => {
        if (nameAvailability) {
            handleNameValidation(name, owner);
        }
    }, [nameAvailability, handleNameValidation]);

    const isFormValid = schemaDefinition.safeParse(formValues).success
        && (!nameAvailability || (!nameValidationState.customError && !nameValidationState.isChecking));

    return {
        formValues,
        setFormValue,
        setFormValues,
        errors,
        isFormValid,
        getFieldProps,
        checkNameAvailability,
    };
};
